import React, { useEffect, useState } from 'react';

import { Control, Controller } from 'react-hook-form';

import { Alert, Autocomplete, Chip, Grid, TextField } from '@mui/material';

import { PromoMeta, sortAllowedPaymentMethods } from '~/services/PromoService';

interface AllowedPaymentMethodSelectProps {
  selectedValues: Array<string>;
  control: Control<any>;
  promoMeta: PromoMeta | null;
  isPromoMetaLoading: boolean;
}

export default function AllowedPaymentMethodSelect({
  selectedValues,
  control,
  promoMeta,
  isPromoMetaLoading,
}: AllowedPaymentMethodSelectProps) {
  if (isPromoMetaLoading || !promoMeta) {
    return null;
  }

  const checkCanValidateBin = () => {
    const allSelectionsSupportBinNumberValidation =
      selectedValues &&
      selectedValues.length > 0 &&
      promoMeta.promo_allowed_payment_methods
        .filter((payment) => selectedValues.includes(payment.method))
        .every((payment) => payment.supportsBinNumberValidation);
    return allSelectionsSupportBinNumberValidation;
  };

  const [canValidateBin, setCanValidateBin] = useState(checkCanValidateBin());

  useEffect(() => {
    setCanValidateBin(checkCanValidateBin());
  }, [selectedValues]);

  return (
    <Grid item xs={12}>
      <Controller
        key="allowed_payment_methods"
        name="allowed_payment_methods"
        control={control}
        render={({ field }) => (
          <Grid item xs={12}>
            <Autocomplete
              {...field}
              multiple
              options={sortAllowedPaymentMethods(promoMeta.promo_allowed_payment_methods).map(
                (provider) => provider.method,
              )}
              getOptionLabel={(option) =>
                sortAllowedPaymentMethods(promoMeta.promo_allowed_payment_methods).find(
                  (payment) => payment.method === option,
                )?.label || option
              }
              renderOption={(props, option) => {
                const paymentMethod = promoMeta.promo_allowed_payment_methods.find(
                  (payment) => payment.method === option,
                );
                return (
                  <li {...props} key={option}>
                    <span
                      style={{
                        textDecoration: paymentMethod?.deprecated ? 'line-through' : 'none',
                      }}
                    >
                      {paymentMethod?.label || option}
                    </span>
                  </li>
                );
              }}
              renderTags={(value, getTagProps) =>
                value.map((option, index) => {
                  const paymentMethod = promoMeta.promo_allowed_payment_methods.find(
                    (payment) => payment.method === option,
                  );
                  return (
                    <Chip
                      {...getTagProps({ index })}
                      key={option}
                      label={
                        <span
                          style={{
                            textDecoration: paymentMethod?.deprecated ? 'line-through' : 'none',
                          }}
                        >
                          {paymentMethod?.label || option}
                        </span>
                      }
                    />
                  );
                })
              }
              renderInput={(params) => <TextField {...params} label="Allowed Payment Methods" variant="outlined" />}
              onChange={(e: unknown, newValue: Array<string> | null) => {
                field.onChange(newValue);
              }}
            />
          </Grid>
        )}
      />
      {canValidateBin && <Alert severity="success">This promo code can validate BINs.</Alert>}
    </Grid>
  );
}
